/**
 * ReadinessCard — Interactive Prüfungsreife-Score component
 * Each component is clickable with a specific CTA.
 * Shows weekly score delta for motivation.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Flame,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  Play,
  Dumbbell,
} from 'lucide-react';
import { Card } from './UIComponents';
import type { UserProfile } from '../../shared/types';
import type { ReadinessResult } from '../services/readinessScore';
import { calculateReadiness, READINESS_LEVELS } from '../services/readinessScore';

// ── Score Delta Tracking ──────────────────────────────────────
const READINESS_HISTORY_KEY = 'fahryo_readiness_history';

interface ReadinessSnapshot {
  score: number;
  date: string; // ISO date (YYYY-MM-DD)
}

function getScoreDelta(currentScore: number): number | null {
  try {
    const stored = localStorage.getItem(READINESS_HISTORY_KEY);
    const history: ReadinessSnapshot[] = stored ? JSON.parse(stored) : [];
    const today = new Date().toISOString().split('T')[0];

    // Find entry from ~7 days ago
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const oldEntry = history.find((h) => h.date <= weekAgo) || history[0];

    // Save today's score (update if exists, append if new)
    const todayIndex = history.findIndex((h) => h.date === today);
    if (todayIndex >= 0) {
      history[todayIndex].score = currentScore;
    } else {
      history.push({ score: currentScore, date: today });
    }
    // Keep only last 30 days
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const trimmed = history.filter((h) => h.date >= cutoff);
    localStorage.setItem(READINESS_HISTORY_KEY, JSON.stringify(trimmed));

    if (!oldEntry || oldEntry.date === today) return null;
    return currentScore - oldEntry.score;
  } catch {
    return null;
  }
}

// ── Interactive MiniBar ──────────────────────────────────────
const MiniBar: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ label, value, icon, color, actionLabel, onAction }) => (
  <button
    onClick={onAction}
    disabled={!onAction}
    className={`flex items-center gap-2 min-w-0 w-full text-left ${onAction ? 'group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-1 px-1 py-1 rounded-lg transition-colors' : ''}`}
  >
    <div className="text-gray-400 flex-shrink-0 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{label}</span>
        <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 ml-1">
          {value}%
        </span>
      </div>
      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      {actionLabel && (
        <div className="text-[9px] text-primary font-bold mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
          <Play size={7} className="fill-primary" /> {actionLabel}
        </div>
      )}
    </div>
  </button>
);

// ── Main Component ──────────────────────────────────────────
interface ReadinessCardProps {
  user: UserProfile;
  compact?: boolean;
  expandable?: boolean; // For LearnView: starts compact, can expand
  onTapWeakChapter?: (chapterId: number) => void;
  onAction?: (action: 'simulation' | 'coverage' | 'hard' | 'srs') => void;
}

const ReadinessCard: React.FC<ReadinessCardProps> = ({
  user,
  compact = false,
  expandable = false,
  onTapWeakChapter,
  onAction,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!expandable);

  const result: ReadinessResult = useMemo(
    () => calculateReadiness(user),
    [
      user.simulationHistory,
      user.chapterProgress,
      user.correctlyAnswered,
      user.streak,
      user.srsData,
    ]
  );

  const levelConfig = READINESS_LEVELS[result.level];
  const circumference = 2 * Math.PI * 15.9155;
  const dashOffset = circumference - (result.score / 100) * circumference;

  // Score delta (weekly comparison)
  const scoreDelta = useMemo(() => getScoreDelta(result.score), [result.score]);

  // Compact mode: ring + score + level + optional expand toggle
  if (compact && !isExpanded) {
    return (
      <button
        onClick={expandable ? () => setIsExpanded(true) : undefined}
        className={`w-full text-left ${expandable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      >
        <div className="flex items-center gap-4 glass-card p-4 w-full">
          <div className="relative w-14 h-14 flex-shrink-0">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-gray-100 dark:text-gray-800"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                style={{ stroke: levelConfig.color, transition: 'stroke-dasharray 1.5s ease-out' }}
                strokeDasharray={`${result.score}, 100`}
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-black" style={{ color: levelConfig.color }}>
                {result.score}%
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-500 dark:text-gray-400">
                {t('readiness_title')}
              </span>
              {scoreDelta !== null && scoreDelta !== 0 && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    scoreDelta > 0
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                  }`}
                >
                  {scoreDelta > 0
                    ? t('readiness_delta_up', { delta: scoreDelta })
                    : t('readiness_delta_down', { delta: scoreDelta })}
                </span>
              )}
            </div>
            <div
              className="text-lg font-black dark:text-white"
              style={{ color: levelConfig.color }}
            >
              {levelConfig.emoji} {t(`readiness_level_${result.level}`)}
            </div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
              {t(result.recommendation)}
            </div>
          </div>
          {expandable && (
            <ChevronDown size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
          )}
        </div>
      </button>
    );
  }

  // Full mode: Ring + breakdown + weak chapters + CTAs
  return (
    <Card className="relative overflow-hidden">
      {/* Decorative gradient */}
      <div
        className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${levelConfig.bgClass} opacity-5 rounded-bl-full`}
      />

      {/* Collapse toggle for expandable mode */}
      {expandable && (
        <button
          onClick={() => setIsExpanded(false)}
          className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center z-10 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <ChevronDown size={14} className="text-gray-400 rotate-180" />
        </button>
      )}

      <div className="flex items-start gap-4">
        {/* SVG Ring */}
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg className="w-full h-full transform -rotate-90 drop-shadow-sm" viewBox="0 0 36 36">
            <path
              className="text-gray-100 dark:text-gray-800"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <path
              style={{
                stroke: levelConfig.color,
                strokeDasharray: `${circumference}`,
                strokeDashoffset: `${dashOffset}`,
                transition: 'stroke-dashoffset 1.5s ease-out',
              }}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-black" style={{ color: levelConfig.color }}>
              {result.score}%
            </span>
          </div>
        </div>

        {/* Text + Delta */}
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              {t('readiness_title')}
            </span>
            {scoreDelta !== null && scoreDelta !== 0 && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-in fade-in duration-500 ${
                  scoreDelta > 0
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                }`}
              >
                {scoreDelta > 0 ? (
                  <>
                    <TrendingUp size={9} className="inline mr-0.5" />
                    {t('readiness_delta_up', { delta: scoreDelta })}
                  </>
                ) : (
                  <>
                    <TrendingDown size={9} className="inline mr-0.5" />
                    {t('readiness_delta_down', { delta: scoreDelta })}
                  </>
                )}
              </span>
            )}
          </div>
          <div
            className="text-base font-black dark:text-white mb-1"
            style={{ color: levelConfig.color }}
          >
            {levelConfig.emoji} {t(`readiness_level_${result.level}`)}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            {t(result.recommendation)}
          </p>
        </div>
      </div>

      {/* Component Breakdown — Interactive */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
        <MiniBar
          label={t('readiness_sim')}
          value={result.components.simulation}
          icon={<Target size={12} />}
          color={levelConfig.color}
          actionLabel={onAction ? t('readiness_action_simulation') : undefined}
          onAction={onAction ? () => onAction('simulation') : undefined}
        />
        <MiniBar
          label={t('readiness_coverage')}
          value={result.components.coverage}
          icon={<TrendingUp size={12} />}
          color={levelConfig.color}
          actionLabel={onAction ? t('readiness_action_coverage') : undefined}
          onAction={onAction ? () => onAction('coverage') : undefined}
        />
        <MiniBar
          label={t('readiness_hard')}
          value={result.components.hardAccuracy}
          icon={<Dumbbell size={12} />}
          color={levelConfig.color}
          actionLabel={onAction ? t('readiness_action_hard') : undefined}
          onAction={onAction ? () => onAction('hard') : undefined}
        />
        <MiniBar
          label={t('readiness_streak')}
          value={result.components.consistency}
          icon={<Flame size={12} />}
          color={levelConfig.color}
        />
        <MiniBar
          label={t('readiness_srs')}
          value={result.components.srsHealth}
          icon={<RefreshCw size={12} />}
          color={levelConfig.color}
          actionLabel={onAction ? t('readiness_action_srs') : undefined}
          onAction={onAction ? () => onAction('srs') : undefined}
        />
      </div>

      {/* Weakest Chapters */}
      {result.weakestChapters.some((c) => c.progress < 80) && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-1 mb-2">
            <AlertTriangle size={11} className="text-amber-500" />
            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
              {t('readiness_weak_chapters')}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {result.weakestChapters
              .filter((c) => c.progress < 80)
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => onTapWeakChapter?.(c.id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold rounded-lg border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors active:scale-95"
                >
                  <span>{t(`chapter_${c.id}_title`)}</span>
                  <span className="text-amber-400">{c.progress}%</span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Disclaimer — readiness is a learning indicator, not an official assessment */}
      <p className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-400 dark:text-gray-500 leading-snug">
        {t('readiness_disclaimer')}
      </p>
    </Card>
  );
};

export default ReadinessCard;
